import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
} from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  Grid,
  Slide,
  Stack,
} from '@mui/material';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { startOfDay, addDays, subDays } from 'date-fns';
import {
  ReasonModal,
  PageLayout,
  PageHeader,
  PageHeaderRow,
  PageTitle,
  PageActionGroup,
  PageBody,
  QuickFilters,
  DynamicFilterModal,
  ErrorBoundary,
} from '@dispatch/web/ui';
import {
  useMap,
  DynamicFilters,
  useGetQualifiedResources,
} from '@dispatch/shared/hooks';
import { useNotification } from '@dispatch/web/ui';
import { MapPopupInfo, TaskInfo } from '@dispatch/shared/types';
import { padClaimNumber } from '@dispatch/shared';
import { DispatchMap } from '@dispatch/web/features/tasks';
import { Grid as BryntumGrid } from '@bryntum/schedulerpro';
import { SchedulerProvider } from './context/SchedulerContext';
import { UnassignedGrid } from './components/UnassignedGrid';
import { SchedulerCore } from './components/SchedulerCore';
import { SchedulerToolbar } from './components/SchedulerToolbar';
import { DragDropSetup } from './components/DragDropSetup';
import { ClaimSearchScroller } from './components/ClaimSearchScroller';
import { ClaimSearchChips } from './components/ClaimSearchChips';
import {
  centerWeekOnDate,
  pickClosestEventToToday,
} from './utils/claimSearchHelpers';
import {
  SchedulerPageProps,
  SchedulerDateRange,
  ColumnsState,
  PendingSchedulerEvent,
} from './types/scheduler.types';
import { eventTooltipStyles } from './components/EventTooltip';

// localStorage keys for persistence
const TIMEZONE_STORAGE_KEY = 'scheduler-timezone';
const WORKING_DAY_START_KEY = 'scheduler-working-day-start';
const WORKING_DAY_END_KEY = 'scheduler-working-day-end';

// Accounts for page header + toolbar chrome above the scheduler/map area
const SCHEDULER_VIEWPORT_HEIGHT = 'calc(100vh - 268px)';

// Safety net for the pending-event placeholder. The id-based match clears
// it the moment the refetched real event arrives; this timer is the
// fallback for cases where the refetch never delivers a matching event
// (silent server drop, schema rename, etc.) so the placeholder doesn't
// stick on screen forever.
const PENDING_EVENT_SAFETY_TIMEOUT_MS = 15_000;

export function SchedulerPage({
  // Data props
  resources,
  events,
  calendars,
  unassignedTasks,
  rescheduleReasons,
  eventCategories,
  canEditEvents,
  canCreateEvents,
  isLoadingSchedule,
  isLoadingUnassignedTasks,
  isFetchingNextPage,
  hasNextPage,
  loadMoreResources,
  refetchAll,
  // Event handler props
  handleEventUpdate,
  handleEventUpdateWithReason,
  handleEventCreate,
  handleEventDelete,
  handleTaskSchedule,
  rescheduleModal,
  closeRescheduleModal,
  isProcessing,
  // Filter props
  filters,
  quickFilters,
  setFilters,
  dynamicFilters,
  quickFilterOptions,
  storedFiltersWithGroups,
  handleSaveFilters,
  filterOrderIndexBulkUpdate,
  groupsOrderIndexBulkUpdateHandler,
  handleSaveFilterGroup,
  handleDeleteFilter,
  handleDeleteFilterGroup,
  setLossStateFilterInput,
  setRsrcStateFilterInput,
  // Company props
  uiValidationRules,
  featureFlags,
  isAdjuster,
  // Date/time props
  dateRange,
  setDateRange,
  timeZone,
  setTimeZone,
  timeZoneOptions,
  // Map props
  multiSelected,
  isLoadingMapResources,
  onResourceSelectionChange,
}: SchedulerPageProps) {
  const fullScreenHandle = useFullScreenHandle();
  const navigate = useNavigate();
  const { setPopupInfo } = useMap();
  const columnsState = useMemo<ColumnsState>(
    () => ({
      team: { hidden: false },
      county: { hidden: false },
    }),
    []
  );

  const [showMap, setShowMap] = useState(false);
  const [showNonWorkingTime, setShowNonWorkingTime] = useState(true);

  // Working day time span — localStorage → feature flag → hardcoded defaults
  const [workingDayStartHour, setWorkingDayStartHour] = useState<number>(() => {
    const stored = localStorage.getItem(WORKING_DAY_START_KEY);
    if (stored !== null) {
      const parsed = Number(stored);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return featureFlags?.collapseNonWorkingTimeConfig?.fromHour ?? 5;
  });
  const [workingDayEndHour, setWorkingDayEndHour] = useState<number>(() => {
    const stored = localStorage.getItem(WORKING_DAY_END_KEY);
    if (stored !== null) {
      const parsed = Number(stored);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return featureFlags?.collapseNonWorkingTimeConfig?.toHour ?? 21;
  });

  // Sync from featureFlags once loaded when no localStorage override exists.
  // useState initializers only run once — if featureFlags was still loading on
  // first render, the state would be stuck at the hardcoded 5/21 defaults.
  useEffect(() => {
    if (
      localStorage.getItem(WORKING_DAY_START_KEY) === null &&
      featureFlags?.collapseNonWorkingTimeConfig?.fromHour != null
    ) {
      setWorkingDayStartHour(
        featureFlags.collapseNonWorkingTimeConfig.fromHour
      );
    }
    if (
      localStorage.getItem(WORKING_DAY_END_KEY) === null &&
      featureFlags?.collapseNonWorkingTimeConfig?.toHour != null
    ) {
      setWorkingDayEndHour(featureFlags.collapseNonWorkingTimeConfig.toHour);
    }
  }, [featureFlags]);

  // Extract claim number from the dynamic filter context's taskFilters sub-object.
  // `filters` is typed as TasksListFilter (flat) but at runtime contains nested
  // { taskFilters: {...}, resourceFilters: {...} } from the DynamicFilters context.
  // This is the API-ready value (zero-padded when the company requires it) —
  // used for event matching in ClaimSearchScroller and auto-navigate effects.
  const taskClaimNumber = (filters as DynamicFilters)?.taskFilters
    ?.claimNumber as string | undefined;

  // The raw value the user typed, kept separate so the search input can show
  // what was typed rather than the padded form after TasksList↔Scheduler nav.
  // Fallback to the padded value covers saved filters / deep links where only
  // the API value exists.
  const searchDisplayValue =
    ((filters as DynamicFilters)?.claimNumberRaw as string | undefined) ??
    taskClaimNumber ??
    '';

  const [showFilterState, setShowFilterState] = useState<
    Record<string, boolean>
  >({
    task_filters: true,
    resource_filters: true,
  });

  const [selectedTask, setSelectedTask] = useState<TaskInfo | undefined>();
  const [pendingEvent, setPendingEvent] =
    useState<PendingSchedulerEvent | null>(null);

  // Clear the pending blocked-time placeholder the moment the refetched
  // events array contains the real event we created. SchedulerCore
  // generates the externalId upfront and stores it in `realTaskId`, so
  // we can swap on stable id equality instead of guessing with a timer.
  // A safety timeout covers the case where the real event never arrives
  // (e.g., silent server drop) so the placeholder doesn't stick.
  useEffect(() => {
    if (!pendingEvent || pendingEvent.kind !== 'event') return;

    const match = events.find((e) => e.id === pendingEvent.realTaskId);
    if (match) {
      setPendingEvent(null);
      return;
    }

    const timer = setTimeout(
      () => setPendingEvent(null),
      PENDING_EVENT_SAFETY_TIMEOUT_MS
    );
    return () => clearTimeout(timer);
  }, [events, pendingEvent]);

  const { notifyError } = useNotification();
  const { data: qualifiedResources, status: qualifiedStatus } =
    useGetQualifiedResources(selectedTask?.id, !!selectedTask);
  const unassignedGridRef = useRef<{ instance?: BryntumGrid }>(null);

  const hasSetClaimNumberRef = useRef(false);

  const handleSearch = useCallback(
    (value: string) => {
      const trimmedValue = value?.trim() || '';

      if (!trimmedValue && !hasSetClaimNumberRef.current) {
        return;
      }

      let claimNumber: string | null = trimmedValue || null;

      if (claimNumber && uiValidationRules?.padClaimNumber) {
        claimNumber = padClaimNumber(trimmedValue);
      }

      hasSetClaimNumberRef.current = !!claimNumber;

      setFilters?.((prev: DynamicFilters) => ({
        ...prev,
        claimNumber,
        // Preserve the raw user input separately so the search field can
        // redisplay it after nav (TasksList↔Scheduler) without the padded
        // zeros leaking back into view. This key stays at the filter root
        // and is not forwarded to the API by mapSchedulerFilters or
        // TasksList's flatFilters→mappedFilters pick.
        claimNumberRaw: trimmedValue || null,
        taskFilters: {
          ...(prev?.taskFilters || {}),
          claimNumber,
        },
      }));
    },
    [setFilters, uiValidationRules?.padClaimNumber]
  );

  const handleDateRangeChange = useCallback(
    (newRange: SchedulerDateRange) => {
      setDateRange(newRange);
    },
    [setDateRange]
  );

  const navigatedClaimNumberRef = useRef<string | null>(null);

  // Memoize today to prevent re-render loops (startOfDay returns a new object each call).
  // Stable for the lifetime of the component mount — acceptable trade-off vs. midnight drift.
  const today = useMemo(() => startOfDay(new Date()), []);

  // Auto-navigate to a 7-day window centered on the matched task when a
  // claim search is active. Uses events (not calendars) so the visible
  // range stays one week even though the API returns weeks of context
  // tasks alongside the searched claim.
  useEffect(() => {
    const claimNumber = taskClaimNumber;

    if (!claimNumber || navigatedClaimNumberRef.current === claimNumber) {
      return;
    }
    if (isLoadingSchedule) {
      return;
    }

    const matchedEvent = pickClosestEventToToday(events, claimNumber);
    if (!matchedEvent?.startDate) {
      return;
    }

    navigatedClaimNumberRef.current = claimNumber;
    handleDateRangeChange(centerWeekOnDate(matchedEvent.startDate));
  }, [taskClaimNumber, events, isLoadingSchedule, handleDateRangeChange]);

  // Clear navigated claim number and reset date range when search is cleared
  useEffect(() => {
    if (!taskClaimNumber && navigatedClaimNumberRef.current) {
      // Reset date range to current when clearing a claim search
      // Note: 7 days matches the viewPreset's defaultSpan and shiftIncrement
      setDateRange({
        startDate: today,
        endDate: addDays(today, 7),
      });
      navigatedClaimNumberRef.current = null;
    }
  }, [taskClaimNumber, today, setDateRange]);

  useEffect(() => {
    if (quickFilters?.assignedStatus !== 'unassigned') {
      setSelectedTask(undefined);
    }
  }, [quickFilters?.assignedStatus]);

  useEffect(() => {
    if (
      selectedTask &&
      qualifiedStatus === 'success' &&
      qualifiedResources?.length === 0
    ) {
      notifyError('No Qualified Resources Found', '');
    }
  }, [
    selectedTask?.id,
    qualifiedStatus,
    qualifiedResources?.length,
    notifyError,
  ]);

  useEffect(() => {
    if (
      qualifiedResources &&
      qualifiedResources.length > 0 &&
      quickFilters?.assignedStatus === 'unassigned' &&
      selectedTask
    ) {
      // Map qualified resources to filter format
      const qualifiedResourceIds = qualifiedResources.map(
        (r: { Id: string; ResourceName: string }) => ({
          title: r.ResourceName,
          value: r.Id,
        })
      );

      // Update filters with qualified resource IDs
      setFilters?.((prev: DynamicFilters) => ({
        ...prev,
        qualifiedResourceIds,
      }));

      // Jump scheduler to appointment date (day before to day after)
      if (selectedTask.appointment) {
        const appointmentDate = new Date(selectedTask.appointment);
        setDateRange({
          startDate: subDays(appointmentDate, 1),
          endDate: addDays(appointmentDate, 1),
        });
      }
    }
  }, [
    qualifiedResources,
    selectedTask,
    quickFilters?.assignedStatus,
    setFilters,
    setDateRange,
  ]);

  // Handle task click in unassigned grid - triggers qualified resources fetch
  const handleUnassignedTaskClick = useCallback(
    (task: TaskInfo) => {
      // Clear previous selection and filters first
      setSelectedTask(undefined);
      setFilters?.((prev: DynamicFilters) => ({
        ...prev,
        qualifiedResourceIds: [],
      }));

      // Set new selected task (triggers useGetQualifiedResources)
      if (task?.id) {
        setSelectedTask(task);
      }
    },
    [setFilters]
  );

  // Update timezone state and persist to localStorage
  const handleTimeZoneChange = useCallback(
    (tz: string) => {
      setTimeZone(tz);
      localStorage.setItem(TIMEZONE_STORAGE_KEY, tz);
    },
    [setTimeZone]
  );

  // Update working day start hour with validation and persist to localStorage
  const handleWorkingDayStartHourChange = useCallback(
    (hour: number) => {
      setWorkingDayStartHour(hour);
      localStorage.setItem(WORKING_DAY_START_KEY, String(hour));
      // Auto-bump end hour if start >= end (maintain minimum 1-hour span)
      if (hour >= workingDayEndHour) {
        const bumped = hour + 1;
        setWorkingDayEndHour(bumped);
        localStorage.setItem(WORKING_DAY_END_KEY, String(bumped));
      }
    },
    [workingDayEndHour]
  );

  // Update working day end hour with validation and persist to localStorage
  const handleWorkingDayEndHourChange = useCallback(
    (hour: number) => {
      // Ensure end > start
      if (hour <= workingDayStartHour) return;
      setWorkingDayEndHour(hour);
      localStorage.setItem(WORKING_DAY_END_KEY, String(hour));
    },
    [workingDayStartHour]
  );

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refetchAll();
  }, [refetchAll]);

  // Handle fullscreen toggle
  const handleFullScreenToggle = useCallback(() => {
    if (fullScreenHandle.active) {
      fullScreenHandle.exit();
    } else {
      fullScreenHandle.enter();
    }
  }, [fullScreenHandle]);

  // Single click on task - sets popup info for map highlighting
  const handleEventSelect = useCallback(
    (eventData: Record<string, unknown>) => {
      if (
        typeof eventData.lossLat === 'number' &&
        typeof eventData.lossLong === 'number'
      ) {
        setPopupInfo(eventData as MapPopupInfo);
      }
    },
    [setPopupInfo]
  );

  // Drag-drop invalid drop handler (memoized to avoid effect teardown in useDragDrop)
  const handleInvalidDrop = useCallback(
    (message: string) => notifyError('Task was not assigned', message),
    [notifyError]
  );

  // Double click on task - navigate to task details blade
  const handleTaskEdit = useCallback(
    (taskReferenceId: string) => {
      navigate(`/scheduler/${taskReferenceId}`);
    },
    [navigate]
  );

  // Resource selection change - lifts selected resource IDs to shell for map radius
  const handleResourceSelection = useCallback(
    (selection: { id: string; name: string }[]) => {
      const resourceIds = selection.map((s) => s.id);
      onResourceSelectionChange?.(resourceIds);
    },
    [onResourceSelectionChange]
  );

  // Memoize to prevent SchedulerCore re-renders from new object references
  const isWorkingTimeFilteredState = useMemo(
    () => ({
      isNonWorkingTimeFiltered: !showNonWorkingTime,
      setIsNonWorkingTimeFiltered: (filtered: boolean) =>
        setShowNonWorkingTime(!filtered),
    }),
    [showNonWorkingTime]
  );

  // Determine if unassigned grid should be shown
  const showUnassignedGrid = useMemo(
    () =>
      quickFilters?.assignedStatus === 'unassigned' &&
      !featureFlags?.qualifiedResourcesFnol,
    [quickFilters?.assignedStatus, featureFlags?.qualifiedResourcesFnol]
  );

  // Stable reference for map view prop
  const mapView = useMemo(
    () => ({ showMap, view: 'scheduler' as const }),
    [showMap]
  );

  // Compute qualified resource IDs for drag-drop enabling
  const qualifiedResourceIds = useMemo(() => {
    if (!qualifiedResources || qualifiedResources.length === 0) return [];
    return qualifiedResources.map((r: { Id: string }) => r.Id);
  }, [qualifiedResources]);

  // V1 parity: when the qualified-resources check for a selected task
  // succeeds with zero matches, hide the full roster from the scheduler so
  // users aren't left staring at unrelated resources after the toast fires.
  const schedulerResources = useMemo(() => {
    const hasZeroQualified =
      quickFilters?.assignedStatus === 'unassigned' &&
      !!selectedTask &&
      qualifiedStatus === 'success' &&
      qualifiedResources?.length === 0;
    return hasZeroQualified ? [] : resources;
  }, [
    resources,
    quickFilters?.assignedStatus,
    selectedTask,
    qualifiedStatus,
    qualifiedResources?.length,
  ]);

  // V1 parity: if the visible resource list becomes empty, drop any multi-
  // select the map radius was tracking. Otherwise the popover still
  // highlights rows that no longer render.
  useEffect(() => {
    if (schedulerResources.length === 0) {
      onResourceSelectionChange?.([]);
    }
  }, [schedulerResources.length, onResourceSelectionChange]);

  // Handle drop from unassigned grid to scheduler
  const handleDrop = useCallback(
    async (params: {
      taskId: string;
      resourceId: string;
      startDate: string;
      endDate: string;
    }) => {
      await handleTaskSchedule(params.taskId, {
        resourceId: params.resourceId,
        startDate: params.startDate,
        endDate: params.endDate,
        rescheduleReasonCode: 'other',
      });
      // V1 parity: clear the highlighted task + qualified-resource filter
      // once the drop succeeds. Without this the previously selected task
      // stays outlined and its filtered resource list remains applied.
      setSelectedTask(undefined);
      setFilters?.((prev: DynamicFilters) => ({
        ...prev,
        qualifiedResourceIds: [],
      }));
    },
    [handleTaskSchedule, setFilters]
  );

  // V1 parity: on any scheduler shift (next/prev/today), clear the active
  // task selection so its qualified-resource filter and highlight drop.
  const handleSchedulerShift = useCallback(() => {
    setSelectedTask(undefined);
    setFilters?.((prev: DynamicFilters) => ({
      ...prev,
      qualifiedResourceIds: [],
    }));
  }, [setFilters]);

  // Tag events whose claimNumber matches the active claim search with the
  // `searched-claim-event` class so they stand out against the surrounding
  // context tasks the API now returns alongside the searched claim.
  const eventsWithSearchHighlight = useMemo(() => {
    if (!taskClaimNumber) return events;
    const HIGHLIGHT_CLASS = 'searched-claim-event';
    return events.map((event) => {
      const matches =
        (event as { claimNumber?: string }).claimNumber === taskClaimNumber;
      if (!matches) return event;
      const existingCls = (event as { cls?: string }).cls;
      const cls = existingCls
        ? `${existingCls} ${HIGHLIGHT_CLASS}`
        : HIGHLIGHT_CLASS;
      return { ...event, cls };
    });
  }, [events, taskClaimNumber]);

  // Merge pending event with events array for optimistic UI
  // The pending event is a placeholder shown while API call is in progress
  const eventsWithPending = useMemo(() => {
    if (!pendingEvent) return eventsWithSearchHighlight;
    const isBlockedTime = pendingEvent.kind === 'event';
    // Create a minimal event object that Bryntum can render as a placeholder
    // Use type assertion since most fields are only needed for the real event
    const pendingAsEvent = {
      id: pendingEvent.id,
      name: pendingEvent.name, // For tasks this is the claim number; for events it's the event name
      startDate: pendingEvent.startDate,
      endDate: pendingEvent.endDate,
      resourceId: pendingEvent.resourceId,
      cls: pendingEvent.cls,
      type: isBlockedTime ? ('event' as const) : ('task' as const),
      // Match real event iconography so the placeholder doesn't look
      // different in kind from what it's about to be replaced with.
      // Blocked-time events render a calendar icon (see
      // useScheduleDataInfinite.mapIcons fallback).
      iconCls: isBlockedTime ? 'b-fa b-fa-calendar' : '',
      // Placeholder values for required fields (not displayed for pending events)
      insuredName: '',
      claimNumber: isBlockedTime ? '' : pendingEvent.name,
      isRescheduleAllowed: false,
      driveStartTime: pendingEvent.startDate,
      homeArrivalTime: pendingEvent.endDate,
      peril: '',
      taskReferenceId: pendingEvent.realTaskId,
      eventName: pendingEvent.name,
      source: 'pending',
      notes: '',
      targetQueue: '',
      isTask: !isBlockedTime,
      reasonCode: '',
      lossLat: 0,
      lossLong: 0,
      mustStartAfter: null,
      mustEndBefore: null,
      methodOfInspection: null,
      taskType: null,
      category: null,
      isForced: null,
      createdBy: null,
      editedBy: null,
    };
    return [...eventsWithSearchHighlight, pendingAsEvent];
  }, [eventsWithSearchHighlight, pendingEvent]);

  return (
    <SchedulerProvider initialTimeZone={timeZone} isMapOpen={showMap}>
      <style>{eventTooltipStyles}</style>
      <PageLayout>
        <PageHeader>
          <PageHeaderRow underline>
            <PageTitle
              title="Scheduler"
              onSearch={handleSearch}
              placeHolderText="Claim Number"
              searchInitialValue={searchDisplayValue}
            />
            <PageActionGroup>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ backgroundColor: 'grey.900' }}
              />
              <FormControlLabel
                control={
                  <Switch
                    color="success"
                    name="isMapView"
                    checked={showMap}
                    onChange={(e) => setShowMap(e.target.checked)}
                  />
                }
                label="Map"
              />
            </PageActionGroup>
          </PageHeaderRow>
          <PageHeaderRow>
            <Stack direction="row" spacing={2} alignItems="center">
              <QuickFilters
                defaultValue={quickFilters?.assignedStatus || 'all'}
                filterKey="assignedStatus"
                quickFilters={quickFilterOptions}
              />
              <ClaimSearchChips
                claimNumber={taskClaimNumber}
                events={events}
                resources={resources}
                onSelectEvent={(event) => {
                  if (event.startDate) {
                    setDateRange(centerWeekOnDate(event.startDate));
                  }
                }}
              />
            </Stack>
            <PageActionGroup>
              <DynamicFilterModal
                filters={dynamicFilters}
                storedFilters={storedFiltersWithGroups}
                showFilterState={{ showFilterState, setShowFilterState }}
                handleSaveFilters={handleSaveFilters}
                handleOrderIndexBulkUpdate={filterOrderIndexBulkUpdate}
                handleGroupsOrderIndexBulkUpdate={
                  groupsOrderIndexBulkUpdateHandler
                }
                handleSaveFilterGroup={handleSaveFilterGroup}
                handleDeleteFilter={handleDeleteFilter}
                handleDeleteFilterGroup={handleDeleteFilterGroup}
                setLossStateFilterInput={setLossStateFilterInput}
                setRsrcStateFilterInput={setRsrcStateFilterInput}
                allowSaveFilters={true}
                syncRoutes={['tasks', 'scheduler']}
              />
            </PageActionGroup>
          </PageHeaderRow>
        </PageHeader>
        <PageBody>
          <Grid container spacing={2} sx={{ overflow: 'hidden' }}>
            <Grid item xs={showMap ? 9 : 12}>
              <FullScreen handle={fullScreenHandle}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: fullScreenHandle.active
                      ? '100%'
                      : SCHEDULER_VIEWPORT_HEIGHT,
                    width: '100%',
                    maxWidth: '100vw',
                    bgcolor: 'background.default',
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: showUnassignedGrid ? '5px 0 0 5px' : '5px',
                  }}
                >
                  {(isProcessing ||
                    isFetchingNextPage ||
                    isLoadingSchedule ||
                    (selectedTask && qualifiedStatus === 'loading')) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(255,255,255,0.3)',
                        zIndex: 1000,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        pointerEvents: 'all',
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )}
                  <SchedulerToolbar
                    dateRange={dateRange}
                    timeZone={timeZone || ''}
                    timeZoneOptions={timeZoneOptions}
                    onTimeZoneChange={handleTimeZoneChange}
                    onRefresh={handleRefresh}
                    onFullScreen={handleFullScreenToggle}
                    isFullScreen={fullScreenHandle.active}
                    showNonWorkingTime={showNonWorkingTime}
                    onShowNonWorkingTimeChange={setShowNonWorkingTime}
                    workingDayStartHour={workingDayStartHour}
                    workingDayEndHour={workingDayEndHour}
                    onWorkingDayStartHourChange={
                      handleWorkingDayStartHourChange
                    }
                    onWorkingDayEndHourChange={handleWorkingDayEndHourChange}
                  />
                  <Box
                    id="schedule"
                    sx={{
                      display: 'flex',
                      flex: 1,
                      minHeight: 0,
                      width: '100%',
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                      <ErrorBoundary
                        boundaryName="SchedulerCore"
                        errorMessage="The scheduler encountered an error. You can try refreshing the page or contact support if the issue persists."
                        showDetails={process.env['NODE_ENV'] === 'development'}
                      >
                        <SchedulerCore
                          resources={schedulerResources}
                          events={eventsWithPending}
                          calendars={calendars}
                          timeZone={timeZone || ''}
                          dateRange={dateRange}
                          unassignedTasks={unassignedTasks}
                          rescheduleReasons={rescheduleReasons}
                          eventCategories={eventCategories}
                          columnsState={columnsState}
                          canEditEvents={canEditEvents}
                          canCreateEvents={canCreateEvents}
                          isAdjuster={isAdjuster}
                          isWorkingTimeFilteredState={
                            isWorkingTimeFilteredState
                          }
                          collapseNonWorkingTimeConfig={
                            featureFlags?.collapseNonWorkingTimeConfig
                          }
                          workingDayStartHour={workingDayStartHour}
                          workingDayEndHour={workingDayEndHour}
                          hasNextPage={hasNextPage}
                          isFetchingNextPage={isFetchingNextPage}
                          onLoadMore={loadMoreResources}
                          onDateRangeChange={handleDateRangeChange}
                          onEventUpdate={handleEventUpdateWithReason}
                          onEventCreate={handleEventCreate}
                          onEventDelete={handleEventDelete}
                          onTaskSchedule={handleTaskSchedule}
                          onSelectionChange={handleResourceSelection}
                          onEventSelect={handleEventSelect}
                          onTaskEdit={handleTaskEdit}
                          onShift={handleSchedulerShift}
                          showUnassignedGrid={showUnassignedGrid}
                          onPendingEventChange={setPendingEvent}
                        />
                      </ErrorBoundary>
                    </Box>
                    {showUnassignedGrid && (
                      <UnassignedGrid
                        ref={
                          unassignedGridRef as React.Ref<{ instance: unknown }>
                        }
                        tasks={unassignedTasks}
                        isLoading={isLoadingUnassignedTasks}
                        onTaskClick={handleUnassignedTaskClick}
                        onTaskSchedule={handleTaskSchedule}
                      />
                    )}
                  </Box>
                </Box>
              </FullScreen>
            </Grid>

            <Slide direction="left" in={showMap} mountOnEnter unmountOnExit>
              <Grid item xs={12} sm={12} md={3} lg={3}>
                <Stack
                  width="100%"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    border: 1,
                    borderRadius: 1,
                    borderColor: 'white',
                    width: '100%',
                    height: SCHEDULER_VIEWPORT_HEIGHT,
                    backgroundColor: '#FFFFFF',
                    position: 'relative',
                  }}
                >
                  <DispatchMap
                    multiSelected={showMap ? multiSelected : undefined}
                    view={mapView}
                    loading={isLoadingMapResources}
                  />
                </Stack>
              </Grid>
            </Slide>
          </Grid>
        </PageBody>
      </PageLayout>

      {/* Drag-drop setup for unassigned tasks - only enabled when qualified resources exist */}
      <DragDropSetup
        gridRef={unassignedGridRef}
        timeZone={timeZone || ''}
        onDrop={handleDrop}
        enabled={showUnassignedGrid && qualifiedResourceIds.length > 0}
        onPendingEventChange={setPendingEvent}
        onInvalidDrop={handleInvalidDrop}
      />

      {/* Scroll to searched event when filtering by claim number */}
      <ClaimSearchScroller
        claimNumber={taskClaimNumber}
        events={events}
        isLoading={isLoadingSchedule}
      />

      {/* Reschedule Reason Modal */}
      <ReasonModal
        modalState={rescheduleModal}
        reasonOptions={rescheduleReasons}
        onSubmit={(reason: string) => {
          rescheduleModal.callback?.(reason);
        }}
        onClose={closeRescheduleModal}
      />

      {/* Outlet for nested routes (task details, resource details) */}
      <Outlet />
    </SchedulerProvider>
  );
}

export default SchedulerPage;
